import { BaseGrid } from './BaseGrid';
import { Cell } from './Cell';

export interface FlowDirection {
  dx: number;
  dy: number;
}

export interface ColorScheme {
  empty: string;
  wall: string;
  start: string;
  goal: string;
  open: string;
  closed: string;
  path: string;
  grid: string;
}

export const DEFAULT_COLORS: ColorScheme = {
  empty: '#ffffff',
  wall: '#34495e',
  start: '#27ae60',
  goal: '#e74c3c',
  open: '#3498db',
  closed: '#95a5a6',
  path: '#f39c12',
  grid: '#ecf0f1'
};

export interface RenderConfig {
  canvas: HTMLCanvasElement;
  cellSize: number;
  showGrid: boolean;
  colors: ColorScheme;
}

export abstract class BaseGridRenderer {
  protected canvas: HTMLCanvasElement;
  protected ctx: CanvasRenderingContext2D;
  protected grid: BaseGrid;
  protected cellSize: number;
  protected showGrid: boolean;
  protected colors: ColorScheme;
  protected baseDpr: number;
  protected dpr: number;
  protected zoomScale: number = 1;

  constructor(grid: BaseGrid, config: RenderConfig) {
    this.grid = grid;
    this.canvas = config.canvas;
    this.cellSize = config.cellSize;
    this.showGrid = config.showGrid;
    this.colors = config.colors;
    this.baseDpr = Math.min(window.devicePixelRatio || 1, 2);
    this.dpr = this.baseDpr;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas');
    }
    this.ctx = ctx;

    this.resizeCanvas();
  }

  abstract resizeCanvas(): void;
  abstract render(hoverCell?: Cell | null, previewType?: 'wall' | 'erase' | 'start' | 'goal' | null): void;
  abstract cellFromPoint(x: number, y: number): { row: number; col: number } | null;

  protected clear(): void {
    this.ctx.fillStyle = this.colors.grid;
    this.ctx.fillRect(0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);
  }

  renderPath(path: Cell[]): void {
    for (const cell of path) {
      if (!cell.isStart && !cell.isGoal) {
        cell.state = 3;
      }
    }
    this.render();
  }

  setCellSize(size: number): void {
    this.cellSize = size;
    this.resizeCanvas();
    this.render();
  }

  setShowGrid(show: boolean): void {
    this.showGrid = show;
    this.render();
  }

  setColors(colors: Partial<ColorScheme>): void {
    this.colors = { ...this.colors, ...colors };
    this.render();
  }

  getCellSize(): number {
    return this.cellSize;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  setZoomScale(scale: number): void {
    const cappedScale = Math.min(scale, 3);
    if (Math.abs(this.zoomScale - cappedScale) < 0.01) return;

    this.zoomScale = cappedScale;
    this.dpr = this.baseDpr * this.zoomScale;
    this.resizeCanvas();
    this.render();
  }

  renderFlowField(flowDirections: Map<Cell, FlowDirection>): void {
    for (const [cell, direction] of flowDirections) {
      if (cell.isWalkable && !cell.isGoal) {
        this.renderArrow(cell, direction);
      }
    }
  }

  protected renderArrow(cell: Cell, direction: FlowDirection): void {
    const centerX = this.getCellCenterX(cell);
    const centerY = this.getCellCenterY(cell);

    const magnitude = Math.sqrt(direction.dx * direction.dx + direction.dy * direction.dy);
    if (magnitude === 0) return;

    const normalizedDx = direction.dx / magnitude;
    const normalizedDy = direction.dy / magnitude;

    const arrowLength = this.cellSize * 0.35;
    const arrowHeadSize = this.cellSize * 0.2;

    const startX = centerX - normalizedDx * arrowLength * 0.5;
    const startY = centerY - normalizedDy * arrowLength * 0.5;
    const endX = centerX + normalizedDx * arrowLength * 0.5;
    const endY = centerY + normalizedDy * arrowLength * 0.5;

    this.ctx.save();
    this.ctx.strokeStyle = '#2c3e50';
    this.ctx.fillStyle = '#2c3e50';
    this.ctx.lineWidth = 2 / this.zoomScale;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.ctx.beginPath();
    this.ctx.moveTo(startX, startY);
    this.ctx.lineTo(endX, endY);
    this.ctx.stroke();

    const angle = Math.atan2(normalizedDy, normalizedDx);
    const headAngle1 = angle + Math.PI * 0.75;
    const headAngle2 = angle - Math.PI * 0.75;

    this.ctx.beginPath();
    this.ctx.moveTo(endX, endY);
    this.ctx.lineTo(
      endX + Math.cos(headAngle1) * arrowHeadSize,
      endY + Math.sin(headAngle1) * arrowHeadSize
    );
    this.ctx.moveTo(endX, endY);
    this.ctx.lineTo(
      endX + Math.cos(headAngle2) * arrowHeadSize,
      endY + Math.sin(headAngle2) * arrowHeadSize
    );
    this.ctx.stroke();
    this.ctx.restore();
  }

  protected getDirectionColor(dx: number, dy: number): string {
    if (dx === 0 && dy < 0) return '#e74c3c';
    if (dx === 0 && dy > 0) return '#9b59b6';
    if (dx > 0 && dy === 0) return '#3498db';
    if (dx < 0 && dy === 0) return '#1abc9c';
    if (dx > 0 && dy < 0) return '#e67e22';
    if (dx > 0 && dy > 0) return '#8e44ad';
    if (dx < 0 && dy < 0) return '#16a085';
    if (dx < 0 && dy > 0) return '#2980b9';
    return '#7f8c8d';
  }

  protected getCellCenterX(cell: Cell): number {
    return cell.col * this.cellSize + this.cellSize / 2;
  }

  protected getCellCenterY(cell: Cell): number {
    return cell.row * this.cellSize + this.cellSize / 2;
  }
}
